import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from "react";
import { useLocation } from "react-router";
import {
  BtSdkAdsCanaryConfig,
  isBtSdkAdsVirtualArticleNavigationState,
  resolveBtSdkAdsCanarySessionConfig,
} from "@util/sdkAdsConfig";

type BtSdkAdsCanaryConfigContextValue = BtSdkAdsCanaryConfig | undefined;

const BtSdkAdsCanaryConfigContext =
  createContext<BtSdkAdsCanaryConfigContextValue>(undefined);

export function BtSdkAdsCanaryConfigProvider({
  children,
}: PropsWithChildren): React.ReactElement {
  const { pathname, state } = useLocation();

  // Latch the resolved canary config so it survives the client-side
  // navigations the article page fires while infinite-scrolling. Those
  // navigations land on sibling article pathnames that are not configured
  // canary paths, so a plain pathname lookup would resolve `undefined` and tear
  // down the SDK shell (and re-enable the legacy ad stack) mid-scroll. The
  // virtual-nav state tag (see useHookUpdateUrlArticle) tells us to keep the
  // latched config instead. A real navigation carries no tag, so the canary
  // clears and the legacy stack resumes.
  const [latchedConfig, setLatchedConfig] = useState(() =>
    resolveBtSdkAdsCanarySessionConfig({
      pathname,
      currentConfig: undefined,
      isVirtualArticleNavigation: false,
    })
  );

  const resolvedConfig = resolveBtSdkAdsCanarySessionConfig({
    pathname,
    currentConfig: latchedConfig,
    isVirtualArticleNavigation: isBtSdkAdsVirtualArticleNavigationState(state),
  });

  useEffect(() => {
    setLatchedConfig(resolvedConfig);
  }, [resolvedConfig]);

  return (
    <BtSdkAdsCanaryConfigContext.Provider value={resolvedConfig}>
      {children}
    </BtSdkAdsCanaryConfigContext.Provider>
  );
}

export function useBtSdkAdsCanaryConfigContext(): BtSdkAdsCanaryConfigContextValue {
  return useContext(BtSdkAdsCanaryConfigContext);
}
